import React, { useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { composeClassName } from '@/utils/util_helpers';
import CustomTabs from './CustomTabs';
import HouseOverview from '../house-overview';
import FloorPlanViewer from '../floor-plan-viewer';
import { ColocationMap } from '@/components/design-system/atoms/map';
import ApplyGuidelines from '../content';
import { format } from 'date-fns';
import { useWindowDimensions } from '@/hooks/use-window-dimensions';
import MARKER_HTML from '@/components/design-system/atoms/map/Marker';

interface HouseTabsProps {
  className?: string;
  houseData: {
    slug: string;
    isWatched: boolean;
    title: string;
    address: string;
    description_html: string;
    about_neighbourhood_html?: string;
    stats: {
      bedrooms: number;
      bathrooms: number;
      toilets: number;
      livable_space: number;
      launch_date: string;
    };
    house_info: {
      rent: {
        cents: number;
        currency_iso: string;
      };
      min_rent: {
        cents: number;
        currency_iso: string;
      };
      max_rent: {
        cents: number;
        currency_iso: string;
      };
      charges: {
        cents: number;
        currency_iso: string;
      };
      pre_registered: number;
    };
    location: {
      latitude: number;
      longitude: number;
    };
    floor_plans: Array<{
      src: string;
      alt: string;
    }>;
    amenities: Array<{
      name: {
        en: string;
        fr: string;
        nl: string;
      };
      icon_type: string;
      icon_svg: string;
    }>;
  };
}

const SECTIONS = {
  OVERVIEW: 'overview-section',
  AMENITIES: 'amenities-section',
  LOCATION: 'location-section',
  FAQ: 'faq-section',
  APPLYGUIDELINES: 'apply-guidelines-section',
} as const;

const HouseTabs: React.FC<HouseTabsProps> = ({ className, houseData }) => {
  const [selectedTab, setSelectedTab] = useState(0);
  const { i18n, t } = useTranslation();
  const { screenIsLargeOrEqualThan } = useWindowDimensions();
  const isMobile = !screenIsLargeOrEqualThan('sm');

  const currentLanguage = i18n.language?.split('-')[0] || 'en';

  if (!houseData) {
    return null;
  }
  // debugger;
  // Convert cents to euros for HouseOverview component
  const formattedHouseInfo = {
    rent: houseData.house_info.rent.cents / 100,
    minRent: houseData.house_info.min_rent.cents / 100,
    maxRent: houseData.house_info.max_rent.cents / 100,
    charges: houseData.house_info.charges.cents / 100,
    preRegistered: houseData.house_info.pre_registered,
  };

  const handleTabChange = useCallback((indexOrEvent: number | React.FormEvent<HTMLDivElement>) => {
    const index = typeof indexOrEvent === 'number' ? indexOrEvent : 0;
    setSelectedTab(index);
    const sections = [SECTIONS.OVERVIEW, SECTIONS.AMENITIES, SECTIONS.LOCATION, SECTIONS.FAQ];
    const section = document.getElementById(sections[index]);
    if (section) {
      const offset = 100;
      const elementPosition = section.getBoundingClientRect().top + window.pageYOffset;
      window.scrollTo({
        top: elementPosition - offset,
        behavior: 'smooth',
      });
    }
  }, []);

  const tabLabels = [
    t('js.components.house_tabs.overview') as string,
    t('js.components.house_tabs.amenities') as string,
    t('js.components.house_tabs.location') as string,
    t('js.components.house_tabs.faq.title') as string,
  ];

  const markers = [
    {
      lat: houseData.location.latitude,
      lng: houseData.location.longitude,
      content: MARKER_HTML,
    },
  ];

  return (
    <div className={composeClassName(className)}>
      <CustomTabs
        tabs={tabLabels}
        selectedIndex={selectedTab}
        onChange={handleTabChange}
        className="mb-4 mt-4 gap-1"
      />

      <div className="space-y-8">
        <section id={SECTIONS.OVERVIEW}>
          <HouseOverview
            title={houseData.title}
            address={houseData.address}
            descriptionHtml={houseData.description_html}
            aboutNeighbourhoodHtml={houseData.about_neighbourhood_html}
            stats={houseData.stats}
            houseInfo={formattedHouseInfo}
            slug={houseData.slug}
            isWatched={houseData.isWatched}
          />
        </section>

        <section id={SECTIONS.AMENITIES}>
          {/* Amenities */}
          <div style={{ borderColor: '#E8E8E8' }} className="mb-7 border-t border-solid pt-7">
            <h2
              className={composeClassName(
                'mb-4 font-bold leading-[30px]',
                isMobile ? 'text-lead1' : 'text-[22px]'
              )}
            >
              {t('js.components.house_tabs.amenities')}
            </h2>
            <div className="grid grid-cols-2 gap-4 gap-y-6 md:grid-cols-4">
              {houseData.amenities.map((amenity) => (
                <div key={amenity.name.en} className="flex items-center gap-3">
                  <div
                    className="flex h-4 w-4 items-center justify-center [&_svg]:h-4 [&_svg]:w-4"
                    dangerouslySetInnerHTML={{ __html: amenity.icon_svg }}
                  />
                  <span
                    className={composeClassName(
                      'font-medium text-N900',
                      isMobile ? 'text-[14px]' : 'text-[16px]'
                    )}
                  >
                    {amenity.name[currentLanguage as keyof typeof amenity.name] || amenity.name.en}
                  </span>
                </div>
              ))}
            </div>
          </div>
        </section>

        <FloorPlanViewer className="mb-8" floorPlans={houseData.floor_plans} />

        <section id={SECTIONS.LOCATION}>
          <div>
            <ColocationMap
              id="house_location_map"
              initialCoordinates={{
                lat: houseData?.location?.latitude,
                lng: houseData?.location?.longitude,
              }}
              markers={markers}
              height="500px"
              className="rounded-lg shadow-sm"
              address={houseData.address}
            />
          </div>
        </section>

        <section id={SECTIONS.FAQ}>
          <div style={{ borderColor: '#E8E8E8' }} className="mb-7 border-t border-solid pt-7">
            <h2
              className={composeClassName(
                'text-h4 font-bold text-text-primary',
                isMobile ? 'mb-2 text-lead1' : 'text-[22px] '
              )}
            >
              {t('js.components.house_tabs.faq.title')}
            </h2>
            <div
              className={composeClassName(
                'space-y-4 font-sans text-text-secondary',
                isMobile ? 'text-[14px]' : 'text-[16px]'
              )}
            >
              <div>
                <h3 className="font-medium text-N900">
                  {t('js.components.house_tabs.faq.rent_included.question')}
                </h3>
                <p className="text-N700">
                  {t('js.components.house_tabs.faq.rent_included.answer')}
                </p>
              </div>
              <div>
                <h3 className="font-medium text-N900">
                  {t('js.components.house_tabs.faq.availability.question')}
                </h3>
                <p className="text-N700">
                  {t('js.components.house_tabs.faq.availability.answer', {
                    date: houseData.stats.launch_date
                      ? format(new Date(houseData.stats.launch_date), 'd MMMM yyyy')
                      : '',
                  })}
                </p>
              </div>
            </div>
          </div>
        </section>

        <section id={SECTIONS.APPLYGUIDELINES}>
          <ApplyGuidelines />
        </section>
      </div>
    </div>
  );
};

export default HouseTabs;
